import { useContext } from 'react';

import type { Control, FieldValues, Path } from 'react-hook-form';

import {
  // Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';

import { Input } from '@/components/ui/input';

import { ReadOnlyField } from '@/lib/pages/forms/components';

import { PermissionsContext } from '@/lib/layout/PermissionContext';
// import { toast } from "@/components/ui/use-toast"

function CustomInput<T extends FieldValues>({
  control,
  // options,
  name,
  label,
  placeholder,
  min,
  max,
  required = false,
  nativeRequired = false,
  orientation = 'horizontal',
  type = 'text',
  step = 1,
  displayRange,
  disabled,
  className,
  labelClassName,
  wideLabel,
  stretch = false,
  readOnly = false,
  overwriteLabelWidth,
  overwriteInputWidth,
  pattern,
  trim = false,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<T>;
  // options: string[];
  name: Path<T>;
  label: string;
  placeholder?: string;
  orientation?: 'horizontal' | 'vertical';
  required?: boolean;
  nativeRequired?: boolean;
  type?:
    | 'text'
    | 'number'
    | 'email'
    | 'tel'
    | 'password'
    | 'currency'
    | 'percentage'
    | 'zip';
  min?: string | number;
  max?: string | number;
  step?: string | number;
  displayRange?: boolean;
  // disabled might be used to ignore all browser validation constraints
  disabled?: boolean;
  className?: string;
  labelClassName?: string;
  wideLabel?: boolean;
  stretch?: boolean;
  readOnly?: boolean;
  overwriteLabelWidth?: string;
  overwriteInputWidth?: string;
  pattern?: string;
  trim?: boolean;
}) {
  const { editMode, responsive } = useContext(PermissionsContext);

  const getMask = (type: string) => {
    if (type === 'currency') {
      return 'currency';
    }

    if (type === 'percentage') {
      return 'percentage';
    }

    if (type === 'number') {
      return 'number';
    }

    return undefined;
  };

  const getType = (type: string) => {
    if (type === 'currency') {
      return 'number';
    }

    if (type === 'percentage') {
      return 'number';
    }

    if (type === 'number') {
      return 'number';
    }

    if (type === 'zip') {
      return 'number';
    }

    return type;
  };

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        if (!editMode || readOnly) {
          return (
            <ReadOnlyField
              label={label}
              value={field.value}
              mask={getMask(type)}
              orientation={orientation}
              wideLabel={wideLabel}
              stretch={stretch}
              responsive={responsive}
              overwriteLabelWidth={overwriteLabelWidth}
              overwriteInputWidth={overwriteInputWidth}
            />
          );
        } else {
          return (
            <FormItem
              className={`flex ${orientation === 'horizontal' ? 'items-center gap-4' : 'w-full flex-col'} ${responsive ? 'max-md:w-full max-md:flex-col' : ''}`}
            >
              <FormLabel
                className={`${orientation === 'horizontal' ? `${overwriteLabelWidth ? overwriteLabelWidth : `${stretch ? `${wideLabel ? 'w-2/3' : 'w-1/3'}` : `${wideLabel ? 'w-2/5' : 'w-1/5'}`}`}` : 'mb-2'} font-semibold leading-[1.5rem] ${responsive ? 'max-md:w-full' : ''} ${labelClassName}`}
              >
                {label}
                {required || nativeRequired ? (
                  <span className="text-red-400">*</span>
                ) : (
                  ''
                )}
              </FormLabel>
              <div
                className={`${orientation === 'horizontal' ? `${overwriteInputWidth ? overwriteInputWidth : `${stretch ? `${wideLabel ? 'w-1/3' : 'w-2/3'}` : `${wideLabel ? 'w-1/5' : 'w-2/5'}`}`}` : ''} ${responsive ? 'max-md:w-full' : ''} !mt-0`}
              >
                <FormControl>
                  <Input
                    // defaultValue={field.value}
                    type={getType(type)}
                    placeholder={placeholder}
                    min={min}
                    max={max}
                    step={step || 1}
                    disabled={disabled}
                    // required={nativeRequired || required} // uncomment to enable FE validation
                    required={nativeRequired}
                    {...field}
                    className={`${className} [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none`}
                    pattern={pattern}
                    onBlur={(e) => {
                      // Trim whitespace and remove tabs/newlines if trim is enabled
                      if (trim && typeof e.target.value === 'string') {
                        // Remove tabs and newlines from anywhere in the string, then trim whitespace
                        const cleanedValue = e.target.value
                          .replace(/[\t\n\r]/g, '') // Remove tabs, newlines, and carriage returns
                          .replace(/\s+/g, ' ') // Replace multiple consecutive spaces with a single space
                          .trim(); // Remove leading/trailing whitespace

                        if (cleanedValue !== e.target.value) {
                          field.onChange(cleanedValue);
                        }
                      }
                      field.onBlur();
                    }}
                  />
                </FormControl>
                {displayRange && (
                  <p className="pl-1 pt-1 text-xs text-gray-400">
                    {min && !max ? `Range: ${min} to Unlimited` : ''}
                    {!min && max ? `Maximum: ${max}` : ''}
                    {min && max ? `Range: ${min} to ${max}` : ''}
                  </p>
                )}
                <FormMessage />
              </div>
              {/* <FormDescription>
                This is your public display name.
              </FormDescription> */}
            </FormItem>
          );
        }
      }}
    />
  );
}

export { CustomInput as Input };
